import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { AddressApi } from "../../api/AddressApi";
import type { AddressSearchParams } from "../../generated/dto/AddressSearchParams";
import type { AddressStaff } from "../../generated/dto/AddressStaff";
import type { AddressStaffSearchResult } from "../../generated/dto/AddressStaffSearchResult";
import type { CreateAddress } from "../../generated/dto/CreateAddress";
import type { DeleteAddressStaff } from "../../generated/dto/DeleteAddressStaff";
import type { UpdateAddress } from "../../generated/dto/UpdateAddress";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class AddressApiClient implements AddressApi {
  constructor(private readonly client = useClient()) {}

  async getAddressStaff(id: number): Promise<AddressStaff> {
    const { data } = await this.client.get<unknown>(`/address/staff/${id}`);
    return deserializeCbiDto<AddressStaff>(data, "generated.dto.AddressStaff");
  }

  async createAddressStaff(source: CreateAddress): Promise<AddressStaff> {
    const { data } = await this.client.post<unknown>(`/address/staff`, serializeCbiDto(source, "generated.dto.CreateAddress"));
    return deserializeCbiDto<AddressStaff>(data, "generated.dto.AddressStaff");
  }

  async updateAddressStaff(source: UpdateAddress): Promise<AddressStaff> {
    const { data } = await this.client.put<unknown>(`/address/staff/${source.addressId}`, serializeCbiDto(source, "generated.dto.UpdateAddress"));
    return deserializeCbiDto<AddressStaff>(data, "generated.dto.AddressStaff");
  }

  async deleteAddressStaff(source: DeleteAddressStaff): Promise<void> {
    await this.client.delete(`/address/staff/${source.addressId}`);
  }

  async searchAddressStaff(params: AddressSearchParams, page: number, perPage: number): Promise<PaginationResult<AddressStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/address/staff/search`, serializeCbiDto(params, "generated.dto.AddressSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<AddressStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<AddressStaffSearchResult>>(result.items, "generated.dto.AddressStaffSearchResult") };
    return result;
  }

  async searchAddressStaffFull(params: AddressSearchParams, page: number, perPage: number): Promise<PaginationResult<AddressStaff>> {
    const { data } = await this.client.post<unknown>(`/address/staff/search/full`, serializeCbiDto(params, "generated.dto.AddressSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<AddressStaff>;
    result = { ...result, items: deserializeCbiDto<Array<AddressStaff>>(result.items, "generated.dto.AddressStaff") };
    return result;
  }
}

export const useAddressApi = (): AddressApi => new AddressApiClient();
